import { useEffect, useMemo, useState } from 'react';
import { GroupedOption, MultiSelectFilterProps, MultiSelectOption } from './MultiSelect.types';

type UseMultiSelectFilterParams<ItemType> = {
  id: string;
  persistKey?: string;
  selectorLabel?: string;
  data: ItemType[] | undefined;
  mapOption: (item: ItemType) => MultiSelectOption;
  groupBy?: (item: ItemType) => string;
  groupSort?: (a: [string, MultiSelectOption[]], b: [string, MultiSelectOption[]]) => number;
};

export const useMultiSelectFilter = <ItemType>({
  id,
  data,
  groupBy,
  groupSort,
  mapOption,
  persistKey,
  selectorLabel,
}: UseMultiSelectFilterParams<ItemType>): {
  filterProps: MultiSelectFilterProps;
} => {
  const [selectedOptions, setSelectedOptions] = useState<MultiSelectOption[]>([]);

  useEffect(() => {
    if (!persistKey) return;

    try {
      const saved = sessionStorage.getItem(persistKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          setSelectedOptions(parsed);
        }
      }
    } catch (e) {
      console.warn(`Failed to parse stored data for key: ${persistKey}`, e);
    }
  }, [persistKey]);

  useEffect(() => {
    return () => {
      if (persistKey) {
        sessionStorage.removeItem(persistKey);
      }
    };
  }, [persistKey]);

  useEffect(() => {
    if (persistKey) {
      try {
        sessionStorage.setItem(persistKey, JSON.stringify(selectedOptions));
      } catch (e) {
        console.warn(`Failed to store selected options for key: ${persistKey}`, e);
      }
    }
  }, [selectedOptions, persistKey]);

  const allOptions = useMemo(() => {
    if (!data) return [];

    const mapped = data.map((item) => {
      const base = mapOption(item);
      const group = groupBy?.(item);
      return group ? { ...base, group } : base;
    });

    const hasGroups = mapped.some((opt) => 'group' in opt && opt.group !== undefined);
    if (!hasGroups) return mapped;

    const grouped = mapped.reduce<Record<string, MultiSelectOption[]>>((acc, option) => {
      const groupName = option.group ?? 'Other';
      if (!acc[groupName]) acc[groupName] = [];
      acc[groupName].push(option);
      return acc;
    }, {});

    const sortedGroups = Object.entries(grouped);
    if (groupSort) {
      sortedGroups.sort(groupSort);
    }

    return sortedGroups.map(([label, options]) => ({
      label,
      options,
    })) as GroupedOption[];
  }, [data, mapOption, groupBy, groupSort]);

  return {
    filterProps: {
      id: id as string,
      allOptions,
      selectedOptions,
      setSelectedOptions,
      label: selectorLabel,
    },
  };
};
